import { memo, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faDownload,
  faTable,
  faClockRotateLeft,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import ActionButton from "../ui/ActionButton";
import ChartCard from "./ChartCard";
import { chartPalette } from "./charts/chartPalette";
import { getCostForRequest } from "../../utils/inventory/costUtils";
import { getTimeAgo } from "../../utils/dateUtils";

const FMT = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

// extract material name from listing with fallback chain
const getMaterial = (listing) =>
  listing?.material ||
  listing?.materialType ||
  listing?.material_name ||
  listing?.material_type ||
  "Unknown";

const ReceiverMaterialsOverview = ({
  pieData = [],
  requests = [],
  listingById,
  usersById,
  dateFrom,
  dateTo,
  selectedMaterials: filterMaterials = [],
  darkMode,
}) => {
  const { t } = useTranslation();
  const [selectedMats, setSelectedMats] = useState(new Set());

  // enrich: group delivered requests by material
  const enrichedData = useMemo(() => {
    const agg = new Map();
    for (const r of requests) {
      if (!r?.date_delivered) continue;

      const d = String(r.date_delivered).slice(0, 10);
      if (dateFrom && d < dateFrom) continue;
      if (dateTo && d > dateTo) continue;

      const listing = listingById?.get(String(r.listing_id));
      const mat = getMaterial(listing);

      if (filterMaterials?.length && !filterMaterials.includes(mat)) continue;

      const qty =
        Number(r.quantity_delivered) || Number(r.quantity) || Number(r.qty) || 0;
      const cost = getCostForRequest(r, listing, qty);

      const prev = agg.get(mat) || { material: mat, quantity: 0, cost: 0, deliveries: 0 };
      prev.quantity += qty;
      prev.cost += cost;
      prev.deliveries += 1;
      agg.set(mat, prev);
    }
    return [...agg.values()].sort((a, b) => b.quantity - a.quantity);
  }, [requests, listingById, dateFrom, dateTo, filterMaterials]);

  const totals = useMemo(
    () =>
      enrichedData.reduce(
        (acc, d) => ({
          quantity: acc.quantity + d.quantity,
          cost: acc.cost + d.cost,
          deliveries: acc.deliveries + d.deliveries,
        }),
        { quantity: 0, cost: 0, deliveries: 0 }
      ),
    [enrichedData]
  );

  // pie chart data — use enrichedData for consistency with table
  const chartData = useMemo(
    () => enrichedData.map((d) => ({ name: d.material, value: d.quantity, cost: d.cost, deliveries: d.deliveries })),
    [enrichedData]
  );

  // delivery details — show when materials selected
  const filteredDeliveries = useMemo(() => {
    if (selectedMats.size === 0) return [];
    const rows = [];
    for (const r of requests) {
      if (!r?.date_delivered) continue;

      const d = String(r.date_delivered).slice(0, 10);
      if (dateFrom && d < dateFrom) continue;
      if (dateTo && d > dateTo) continue;

      const listing = listingById?.get(String(r.listing_id));
      const mat = getMaterial(listing);
      if (!selectedMats.has(mat)) continue;

      if (filterMaterials?.length && !filterMaterials.includes(mat)) continue;

      const qty =
        Number(r.quantity_delivered) || Number(r.quantity) || Number(r.qty) || 0;
      const cost = getCostForRequest(r, listing, qty);

      // resolve donor name
      const companyId =
        r.company_id || r.companyId || r.user_id || r.userId || r.donor_id || r.donorId || listing?.company_id;
      const company = usersById?.get(String(companyId));
      const donor =
        company?.company_name || company?.name || listing?.donor_name || listing?.company_name || listing?.company || "—";

      rows.push({
        id: r.id || rows.length,
        donor,
        material: mat,
        quantity: qty,
        cost,
        dateDelivered: r.date_delivered,
      });
    }
    return rows;
  }, [requests, listingById, usersById, selectedMats, dateFrom, dateTo, filterMaterials]);

  const toggleMaterial = (mat) => {
    setSelectedMats((prev) => {
      const next = new Set(prev);
      next.has(mat) ? next.delete(mat) : next.add(mat);
      return next;
    });
  };

  const handleExportCSV = () => {
    const headers = ["Material Type", "Quantity (lbs)", "Cost ($)", "Deliveries"];
    const rows = enrichedData.map((d) => [
      d.material,
      d.quantity.toFixed(2),
      d.cost.toFixed(2),
      d.deliveries,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receiver-materials-overview-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0].payload;
    const pct = totals.quantity ? ((item.value / totals.quantity) * 100).toFixed(1) : "0.0";
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip-label">{item.name}</p>
        <p>{item.value.toLocaleString()} lbs ({pct}%)</p>
        <p>${item.cost.toLocaleString(undefined, FMT)}</p>
        <p>{item.deliveries} {t("deliveries")}</p>
      </div>
    );
  };

  return (
    <div className="rmo">
      <div className="rmo__grid">
        {/* pie chart */}
        <ChartCard
          icon={faChartPie}
          iconColor="green"
          title={t("Material Distribution")}
          badge={chartData.length > 0 ? `${chartData.length} ${t("types")}` : null}
        >
          {chartData.length === 0 ? (
            <div className="chart-empty-state">
              <p>{t("No delivery data for selected period")}</p>
            </div>
          ) : (
            <div className="rmo__pie-container">
              <div className="rmo__pie-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={45}
                    >
                      {chartData.map((entry, i) => (
                        <Cell
                          key={`cell-${i}`}
                          fill={chartPalette[i % chartPalette.length]}
                          opacity={
                            selectedMats.size === 0 || selectedMats.has(entry.name)
                              ? 1
                              : 0.3
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="rmo__legend">
                {chartData.map((entry, i) => (
                  <div
                    key={entry.name}
                    className="rmo__legend-item"
                    onClick={() => toggleMaterial(entry.name)}
                  >
                    <svg width="12" height="12" style={{ flexShrink: 0 }}>
                      <rect
                        width="12"
                        height="12"
                        rx="3"
                        fill={chartPalette[i % chartPalette.length]}
                      />
                    </svg>
                    <span className="rmo__legend-label">{entry.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>

        {/* breakdown table */}
        <ChartCard
          icon={faTable}
          title={t("Materials Breakdown")}
          actions={
            <ActionButton
              variant="secondary"
              icon={faDownload}
              onClick={handleExportCSV}
              disabled={enrichedData.length === 0}
              className="compact-btn"
            >
              {t("Export CSV")}
            </ActionButton>
          }
        >
          <div className="rmo__table-wrap">
            <table className="rmo__table">
              <thead>
                <tr>
                  <th className="rmo__col-check"></th>
                  <th>{t("Material Type")}</th>
                  <th className="rmo__col-num">{t("Quantity (lbs)")}</th>
                  <th className="rmo__col-num">{t("Cost ($)")}</th>
                  <th className="rmo__col-num">{t("Deliveries")}</th>
                </tr>
              </thead>
              <tbody>
                {enrichedData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="rmo__empty-row">
                      {t("No data available")}
                    </td>
                  </tr>
                ) : (
                  enrichedData.map((d, i) => (
                    <tr
                      key={d.material}
                      className={selectedMats.has(d.material) ? "selected" : ""}
                      onClick={() => toggleMaterial(d.material)}
                    >
                      <td className="rmo__col-check">
                        <input
                          type="checkbox"
                          checked={selectedMats.has(d.material)}
                          onChange={() => toggleMaterial(d.material)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="rmo__mat-cell">
                        <div className="rmo__mat-name">
                          <svg width="12" height="12" style={{ flexShrink: 0 }}>
                            <rect
                              width="12"
                              height="12"
                              rx="3"
                              fill={chartPalette[i % chartPalette.length]}
                            />
                          </svg>
                          <span>{d.material}</span>
                        </div>
                      </td>
                      <td className="rmo__col-num">
                        {d.quantity.toLocaleString(undefined, FMT)}
                      </td>
                      <td className="rmo__col-num">
                        ${d.cost.toLocaleString(undefined, FMT)}
                      </td>
                      <td className="rmo__col-num">{d.deliveries}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {enrichedData.length > 0 && (
                <tfoot>
                  <tr className="rmo__totals">
                    <td className="rmo__col-check"></td>
                    <td><strong>{t("Total")}</strong></td>
                    <td className="rmo__col-num">
                      <strong>{totals.quantity.toLocaleString(undefined, FMT)}</strong>
                    </td>
                    <td className="rmo__col-num">
                      <strong>${totals.cost.toLocaleString(undefined, FMT)}</strong>
                    </td>
                    <td className="rmo__col-num">
                      <strong>{totals.deliveries}</strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </ChartCard>
      </div>

      {/* delivery details — conditional */}
      {selectedMats.size > 0 && filteredDeliveries.length > 0 && (
        <div className="rmo__delivery">
          <ChartCard
            icon={faClockRotateLeft}
            title={`${t("Delivery Details")} — ${[...selectedMats].join(", ")}`}
            badge={`${filteredDeliveries.length} ${t("records")}`}
          >
            <div className="rmo__table-wrap">
              <table className="rmo__table rmo__delivery-table">
                <thead>
                  <tr>
                    <th>{t("Donor")}</th>
                    <th>{t("Material")}</th>
                    <th className="rmo__col-num">{t("Quantity (lbs)")}</th>
                    <th className="rmo__col-num">{t("Cost ($)")}</th>
                    <th>{t("Delivered")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeliveries.map((row) => {
                    const ago = getTimeAgo(row.dateDelivered);
                    const full = row.dateDelivered
                      ? new Date(row.dateDelivered).toLocaleDateString()
                      : "—";
                    return (
                      <tr key={row.id}>
                        <td className="rmo__donor-name">{row.donor}</td>
                        <td>{row.material}</td>
                        <td className="rmo__col-num">
                          {row.quantity.toLocaleString(undefined, FMT)}
                        </td>
                        <td className="rmo__col-num">
                          ${row.cost.toLocaleString(undefined, FMT)}
                        </td>
                        <td>
                          <div className="rmo__delivered-cell">
                            <FontAwesomeIcon
                              icon={faTruck}
                              className="rmo__delivered-icon"
                            />
                            <div className="rmo__delivered-info">
                              <span className="rmo__delivered-ago">{ago}</span>
                              <span className="rmo__delivered-date">{full}</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>
      )}
    </div>
  );
};

export default memo(ReceiverMaterialsOverview);
